import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Plus,
  UserPlus,
  Crown,
  Trash2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  Search,
  HeartPulse,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { careTeamsApi } from '@/api/endpoints/careteams.api';
import { careLinesApi } from '@/api/endpoints/carelines.api';
import { usersApi } from '@/api/endpoints/users.api';
import type { CareTeamDto, CreateCareTeamRequest, AddMemberRequest, CareTeamRole } from '@/types/careteam.types';

interface CareTeamCardProps {
  patientId: string;
}

export function CareTeamCard({ patientId }: CareTeamCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [showAddMember, setShowAddMember] = useState<string | null>(null);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [createTeamForm, setCreateTeamForm] = useState<Partial<CreateCareTeamRequest>>({
    name: '',
    description: '',
    careLineId: undefined,
  });
  const [addMemberForm, setAddMemberForm] = useState<Partial<AddMemberRequest>>({
    userId: '',
    role: undefined,
  });
  const [userSearch, setUserSearch] = useState('');

  // Fetch all care teams for the patient
  const { data: careTeams, isLoading } = useQuery({
    queryKey: ['patient-care-teams', patientId],
    queryFn: () => careTeamsApi.getPatientCareTeams(patientId),
  });

  const { data: careLines } = useQuery({
    queryKey: ['care-lines-active'],
    queryFn: () => careLinesApi.getAllActive(),
    enabled: showCreateTeam,
  });

  const { data: availableUsers, isLoading: loadingUsers } = useQuery({
    queryKey: ['users-for-care-team'],
    queryFn: () => usersApi.getAll({ page: 0, size: 100 }),
    enabled: !!showAddMember,
  });

  const createTeamMutation = useMutation({
    mutationFn: careTeamsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-care-teams', patientId] });
      toast({ title: 'Equipe criada com sucesso' });
      setShowCreateTeam(false);
      setCreateTeamForm({ name: '', description: '', careLineId: undefined });
    },
    onError: () => {
      toast({ title: 'Erro ao criar equipe', variant: 'destructive' });
    },
  });

  const handleCreateTeam = () => {
    createTeamMutation.mutate({
      patientId,
      name: createTeamForm.name || undefined,
      description: createTeamForm.description || undefined,
      careLineId: createTeamForm.careLineId || undefined,
    });
  };

  const addMemberMutation = useMutation({
    mutationFn: ({ careTeamId, request }: { careTeamId: string; request: AddMemberRequest }) =>
      careTeamsApi.addMember(careTeamId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-care-teams', patientId] });
      toast({ title: 'Membro adicionado com sucesso' });
      setShowAddMember(null);
      setAddMemberForm({ userId: '', role: undefined });
      setUserSearch('');
    },
    onError: () => {
      toast({ title: 'Erro ao adicionar membro', variant: 'destructive' });
    },
  });

  const setCoordinatorMutation = useMutation({
    mutationFn: careTeamsApi.setCoordinator,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-care-teams', patientId] });
      toast({ title: 'Coordenador definido com sucesso' });
    },
    onError: () => {
      toast({ title: 'Erro ao definir coordenador', variant: 'destructive' });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: careTeamsApi.removeMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-care-teams', patientId] });
      toast({ title: 'Membro removido da equipe' });
    },
    onError: () => {
      toast({ title: 'Erro ao remover membro', variant: 'destructive' });
    },
  });

  const handleAddMember = () => {
    if (!showAddMember || !addMemberForm.userId || !addMemberForm.role) {
      toast({ title: 'Selecione um usuário e uma função', variant: 'destructive' });
      return;
    }
    addMemberMutation.mutate({
      careTeamId: showAddMember,
      request: {
        userId: addMemberForm.userId,
        role: addMemberForm.role,
      },
    });
  };

  const toggleTeamExpanded = (teamId: string) => {
    setExpandedTeams((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(teamId)) {
        newSet.delete(teamId);
      } else {
        newSet.add(teamId);
      }
      return newSet;
    });
  };

  // Filter users based on search
  const filteredUsers = availableUsers?.content
    .filter((u) => u.active)
    .filter((u) => {
      if (!userSearch) return true;
      const search = userSearch.toLowerCase();
      return (
        u.name.toLowerCase().includes(search) ||
        u.email.toLowerCase().includes(search)
      );
    }) || [];

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      PHYSICIAN: 'Médico',
      NURSE: 'Enfermeiro(a)',
      PSYCHOLOGIST: 'Psicólogo(a)',
      NUTRITIONIST: 'Nutricionista',
      SOCIAL_WORKER: 'Assistente Social',
      PHARMACIST: 'Farmacêutico(a)',
      PHYSICAL_THERAPIST: 'Fisioterapeuta',
      OTHER: 'Outro',
    };
    return labels[role] || role;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-100 text-green-800">Ativa</Badge>;
      case 'INACTIVE':
        return <Badge className="bg-yellow-100 text-yellow-800">Inativa</Badge>;
      case 'DISSOLVED':
        return <Badge className="bg-red-100 text-red-800">Dissolvida</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Filter active care teams
  const activeCareTeams = careTeams?.filter((t) => t.status === 'ACTIVE') || [];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-5 w-5" />
            Equipes de Cuidado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-5 w-5" />
              Equipes de Cuidado
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
          <CardDescription>
            {activeCareTeams.length} equipe(s) ativa(s)
          </CardDescription>
        </CardHeader>
        {isOpen && (
          <CardContent>
            {activeCareTeams.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground text-sm mb-3">
                  Nenhuma equipe de cuidado ativa
                </p>
                <Button size="sm" variant="outline" onClick={() => setShowCreateTeam(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Criar Equipe
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {activeCareTeams.map((team) => (
                  <CareTeamItem
                    key={team.id}
                    team={team}
                    isExpanded={expandedTeams.has(team.id)}
                    onToggleExpand={() => toggleTeamExpanded(team.id)}
                    onAddMember={() => setShowAddMember(team.id)}
                    onSetCoordinator={(memberId) => setCoordinatorMutation.mutate(memberId)}
                    onRemoveMember={(memberId) => removeMemberMutation.mutate(memberId)}
                    getInitials={getInitials}
                    getRoleLabel={getRoleLabel}
                    getStatusBadge={getStatusBadge}
                  />
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowCreateTeam(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Criar Nova Equipe
                </Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Create Team Dialog */}
      <Dialog open={showCreateTeam} onOpenChange={setShowCreateTeam}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Equipe de Cuidado</DialogTitle>
            <DialogDescription>
              Crie uma nova equipe de cuidado para este paciente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Nome da Equipe (opcional)</Label>
              <Input
                id="team-name"
                placeholder="Ex: Equipe APS - Diabetes"
                value={createTeamForm.name || ''}
                onChange={(e) =>
                  setCreateTeamForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-description">Descrição (opcional)</Label>
              <Textarea
                id="team-description"
                placeholder="Descrição da equipe..."
                value={createTeamForm.description || ''}
                onChange={(e) =>
                  setCreateTeamForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-careline">Linha de Cuidado (opcional)</Label>
              <Select
                value={createTeamForm.careLineId || '_none'}
                onValueChange={(value) =>
                  setCreateTeamForm((prev) => ({
                    ...prev,
                    careLineId: value === '_none' ? undefined : value,
                  }))
                }
              >
                <SelectTrigger id="team-careline">
                  <SelectValue placeholder="Selecione uma linha de cuidado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Nenhuma</SelectItem>
                  {careLines?.map((careLine) => (
                    <SelectItem key={careLine.id} value={careLine.id}>
                      {careLine.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTeam(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateTeam}
              disabled={createTeamMutation.isPending}
            >
              {createTeamMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Criar Equipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={!!showAddMember} onOpenChange={() => setShowAddMember(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Membro</DialogTitle>
            <DialogDescription>
              Adicione um profissional à equipe de cuidado
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* User Search */}
            <div className="space-y-2">
              <Label>Buscar Profissional</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* User List */}
            <div className="space-y-2">
              <Label>Selecione o Profissional *</Label>
              <div className="max-h-48 overflow-y-auto rounded-lg border">
                {loadingUsers ? (
                  <div className="flex items-center justify-center p-4">
                    <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    {userSearch ? 'Nenhum usuário encontrado' : 'Nenhum usuário disponível'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredUsers.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => setAddMemberForm((prev) => ({ ...prev, userId: user.id }))}
                        className={`flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted ${
                          addMemberForm.userId === user.id ? 'bg-primary/10' : ''
                        }`}
                      >
                        <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                          {getInitials(user.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>
                        {addMemberForm.userId === user.id && (
                          <div className="h-3 w-3 rounded-full bg-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <Label htmlFor="member-role">Função na Equipe *</Label>
              <Select
                value={addMemberForm.role || ''}
                onValueChange={(value) =>
                  setAddMemberForm((prev) => ({ ...prev, role: value as CareTeamRole }))
                }
              >
                <SelectTrigger id="member-role">
                  <SelectValue placeholder="Selecione a função" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PHYSICIAN">Médico</SelectItem>
                  <SelectItem value="NURSE">Enfermeiro(a)</SelectItem>
                  <SelectItem value="PSYCHOLOGIST">Psicólogo(a)</SelectItem>
                  <SelectItem value="NUTRITIONIST">Nutricionista</SelectItem>
                  <SelectItem value="SOCIAL_WORKER">Assistente Social</SelectItem>
                  <SelectItem value="PHARMACIST">Farmacêutico(a)</SelectItem>
                  <SelectItem value="PHYSICAL_THERAPIST">Fisioterapeuta</SelectItem>
                  <SelectItem value="OTHER">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMember(null)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddMember}
              disabled={addMemberMutation.isPending || !addMemberForm.userId || !addMemberForm.role}
            >
              {addMemberMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Sub-component for each care team
interface CareTeamItemProps {
  team: CareTeamDto;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onAddMember: () => void;
  onSetCoordinator: (memberId: string) => void;
  onRemoveMember: (memberId: string) => void;
  getInitials: (name: string) => string;
  getRoleLabel: (role: string) => string;
  getStatusBadge: (status: string) => React.ReactNode;
}

function CareTeamItem({
  team,
  isExpanded,
  onToggleExpand,
  onAddMember,
  onSetCoordinator,
  onRemoveMember,
  getInitials,
  getRoleLabel,
  getStatusBadge,
}: CareTeamItemProps) {
  return (
    <div className="rounded-lg border bg-muted/30">
      <button
        type="button"
        onClick={onToggleExpand}
        className="flex w-full items-center justify-between p-3 text-left hover:bg-muted/50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{team.name || 'Equipe de Cuidado'}</span>
            {getStatusBadge(team.status)}
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
            <span>{team.memberCount} membro(s)</span>
            {team.careLineName && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <HeartPulse className="h-3 w-3" />
                  {team.careLineName}
                </span>
              </>
            )}
            {team.coordinatorName && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Crown className="h-3 w-3 text-yellow-500" />
                  {team.coordinatorName}
                </span>
              </>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t p-3 space-y-2">
          {team.members.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              Nenhum membro na equipe
            </p>
          ) : (
            team.members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-2 rounded-lg bg-background"
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                    {getInitials(member.userName)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{member.userName}</span>
                      {member.isCoordinator && (
                        <Crown className="h-3 w-3 text-yellow-500" />
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {getRoleLabel(member.role)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!member.isCoordinator && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetCoordinator(member.id);
                      }}
                      title="Definir como coordenador"
                    >
                      <Crown className="h-3 w-3" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveMember(member.id);
                    }}
                    title="Remover da equipe"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))
          )}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              onAddMember();
            }}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Adicionar Membro
          </Button>
        </div>
      )}
    </div>
  );
}
